import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Group, MapEntity } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import { exitLassoMode } from "@app/scene/map/store/actions";
import {
  bulkAddPinsToGroup,
  bulkRemovePinsFromGroup,
} from "@app/scene/map/store/groupMode/actions";
import { getGroupId } from "@app/scene/map/store/groupMode/selectors";
import {
  getSelectedEntities,
  getSelectedRecordsCount,
} from "@app/scene/map/store/mapMode/selectors";
import { RootState } from "@app/store/rootReducer";

interface Props {
  addPinsToGroup: typeof bulkAddPinsToGroup.request;
  exitLassoMode: typeof exitLassoMode;
  groupId?: Group["id"];
  records: MapEntity[];
  removePinsFromGroup: typeof bulkRemovePinsFromGroup.request;
  selectedCount: number;
}

const GroupBulkOperations: React.FC<Props> = ({
  addPinsToGroup,
  exitLassoMode,
  groupId,
  records,
  removePinsFromGroup,
  selectedCount,
}) => {
  const intl = useIntl();

  const [recordsInGroup, recordsOutsideGroup] = useMemo(() => {
    const recordsInGroup: Array<MapEntity> = [];
    const recordsOutsideGroup: Array<MapEntity> = [];

    if (records?.length) {
      records.forEach((entity: MapEntity) => {
        const hasGroup =
          groupId !== undefined && !!entity.groups?.find((group: Group) => group.id === groupId);
        (hasGroup ? recordsInGroup : recordsOutsideGroup).push(entity);
      });
    }

    return [recordsInGroup, recordsOutsideGroup];
  }, [groupId, records]);

  const handleAdd = useCallback(() => {
    addPinsToGroup({ entities: recordsOutsideGroup });
    exitLassoMode();
  }, [addPinsToGroup, exitLassoMode, recordsOutsideGroup]);

  const handleRemove = useCallback(() => {
    removePinsFromGroup({ entities: recordsInGroup });
    exitLassoMode();
  }, [exitLassoMode, recordsInGroup, removePinsFromGroup]);

  if (selectedCount < 1) {
    return null;
  }

  return (
    <>
      {recordsInGroup.length === 0 && recordsOutsideGroup.length > 0 && (
        <Button onClick={handleAdd} type="primary">
          {intl.formatMessage({
            id: "map.tool.lasso.toolbar.groupBulk.addToGroup",
            defaultMessage: "Add to Group",
            description: "Add to Group button in lasso bulk operations toolbar",
          })}
        </Button>
      )}

      {recordsInGroup.length > 0 && recordsOutsideGroup.length === 0 && (
        <Button onClick={handleRemove}>
          {intl.formatMessage({
            id: "map.tool.lasso.toolbar.groupBulk.removeFromGroup",
            defaultMessage: "Remove from Group",
            description: "Remove from Group button in lasso bulk operations toolbar",
          })}
        </Button>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  groupId: getGroupId(state),
  records: getSelectedEntities(state),
  selectedCount: getSelectedRecordsCount(state),
});

const mapDispatchToProps = {
  addPinsToGroup: bulkAddPinsToGroup.request,
  exitLassoMode,
  removePinsFromGroup: bulkRemovePinsFromGroup.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(GroupBulkOperations);
